import { useRef } from "react";
import { m, useScroll, useTransform } from "framer-motion";
import {
  ChevronDown,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
} from "lucide-react";
import { BRAND, IMAGES } from "@/lib/site";
import { scrollToId } from "@/lib/scroll";
import OpenStatus from "@/components/OpenStatus";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <m.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </m.span>
  </span>
);

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.08, 1.25]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-center overflow-hidden"
    >
      <m.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="Candle-lit luxury spa treatment room at Orchid Vue Spa Bur Dubai"
          fetchPriority="high"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </m.div>
      <div className="absolute inset-0 bg-plum-950/70" />
      <div className="absolute inset-0 bg-gradient-to-r from-plum-950 via-plum-950/60 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-plum-950 to-transparent" />

      <p
        aria-hidden="true"
        className="absolute right-6 top-1/2 hidden -translate-y-1/2 rotate-90 font-mono text-[10px] uppercase tracking-[0.5em] text-cream/40 xl:block"
      >
        8th Floor — Orchid Vue Hotel
      </p>

      <m.div
        style={{ opacity: fade }}
        className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-28 pt-36 lg:px-10 lg:pt-40"
      >
        <m.p
          initial={{ opacity: 0.01, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-champagne sm:text-xs"
        >
          <MapPin className="h-4 w-4 text-magenta" />
          Orchid Vue Hotel · Bur Dubai · Near Sharaf DG Metro
        </m.p>

        <m.div
          initial={{ opacity: 0.01, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.18, ease: EASE }}
          className="mb-6"
        >
          <OpenStatus />
        </m.div>

        <h1 className="max-w-4xl font-serif text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl">
          <MaskedLine delay={0.2}>Luxury Massage &amp;</MaskedLine>
          <MaskedLine delay={0.32}>
            <span className="italic text-magenta">Spa Sanctuary</span>
          </MaskedLine>
          <MaskedLine delay={0.44}>in Bur Dubai</MaskedLine>
        </h1>

        <m.p
          initial={{ opacity: 0.01, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.6, ease: EASE }}
          className="mt-6 max-w-xl text-base leading-relaxed text-lavender sm:text-lg"
        >
          Moroccan baths, four-hand therapy and authentic Thai massage on the
          8th floor of Orchid Vue Hotel. Fully sanitized private suites, expert
          therapists, and booking in one tap.
        </m.p>

        <m.div
          initial={{ opacity: 0.01, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.72, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-3"
        >
          <a
            data-testid="hero-whatsapp-cta"
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 rounded-full bg-wa px-7 py-4 text-sm font-bold text-plum-950 shadow-[0_0_40px_rgba(37,211,102,0.35)] transition-transform duration-300 hover:scale-[1.04]"
          >
            <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:-rotate-12" />
            Book Instantly on WhatsApp
          </a>
          <a
            data-testid="hero-call-cta"
            href={BRAND.callLink}
            className="flex items-center gap-2.5 rounded-full border border-champagne/40 px-7 py-4 text-sm font-semibold text-champagne transition-colors duration-300 hover:bg-champagne hover:text-plum-950"
          >
            <Phone className="h-4 w-4" />
            Call {BRAND.phoneDisplay}
          </a>
        </m.div>

        <m.div
          initial={{ opacity: 0.01 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.9 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
        >
          {[
            { icon: ShieldCheck, label: "Fully Sanitized Rooms" },
            { icon: Sparkles, label: "Expert Therapists" },
            { icon: Star, label: `${BRAND.rating} on Google · ${BRAND.reviewCount} reviews` },
          ].map(({ icon: Icon, label }) => (
            <span
              key={label}
              className="flex items-center gap-2 text-xs font-medium text-cream/70 sm:text-sm"
            >
              <Icon className="h-4 w-4 text-magenta" />
              {label}
            </span>
          ))}
        </m.div>
      </m.div>

      <m.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToId("#services")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-5 z-10 flex items-center gap-3 lg:left-10"
        aria-label="Scroll to services"
      >
        <m.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="grid h-10 w-10 place-items-center rounded-full border border-magenta/30 text-magenta"
        >
          <ChevronDown className="h-4 w-4" />
        </m.span>
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/70">
          Scroll
        </span>
      </m.button>
    </section>
  );
}
